/**
 * One deal, in a drawer over the board or table: its fields, its stage and
 * its timeline, where every move sits with the notes, tasks and calls on it.
 *
 * Fields save together with Save. The stage is different: changing it is a
 * move, made at once (asking for a reason when it is won or lost), because
 * every move is recorded in the history and should be one deliberate act.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Anchor, Badge, Button, Divider, Drawer, Group, Loader, NumberInput, Select, Stack, Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Link } from 'react-router-dom';
import { useDeal, useDeleteDeal, usePipelines, useUpdateDeal } from '../../api/crm';
import { MonoLabel } from '../../ui';
import { useVocab } from '../../vocab';
import {
  CompanySelect, formatPence, OwnerSelect, poundsToPence, statusColour, STATUS_LABEL, useCrmPermissions,
} from './crmShared';
import { useStageMove } from './DealForms';
import { TimelinePanel } from './Timeline';
import { PropertiesPanel } from '../properties/PropertyFields';

type Draft = {
  name: string;
  amount: number | string;
  close: string;
  owner: string | null;
  companyId: string | null;
  companyName: string | null;
};

export function DealDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const v = useVocab();
  const perms = useCrmPermissions();
  const { data: deal, isPending, error } = useDeal(id);
  const pipelines = usePipelines(!!id).data?.pipelines ?? [];
  const update = useUpdateDeal();
  const remove = useDeleteDeal();
  const mover = useStageMove();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!deal) return;
    setDraft({
      name: deal.name,
      amount: deal.amount_pence / 100,
      close: deal.close_date ?? '',
      owner: deal.owner_user_id ?? null,
      companyId: deal.company_id ?? null,
      companyName: deal.company_name ?? null,
    });
    setFormError('');
  }, [deal]);

  const pipeline = pipelines.find((p) => p.id === deal?.pipeline_id);
  const stages = (pipeline?.stages ?? []).filter((s) => !s.archived || s.id === deal?.stage_id);
  const canEdit = perms.editDeals;

  const dirty = !!deal && !!draft && (
    draft.name.trim() !== deal.name
    || poundsToPence(draft.amount) !== deal.amount_pence
    || (draft.close || null) !== (deal.close_date ?? null)
    || draft.owner !== (deal.owner_user_id ?? null)
    || draft.companyId !== (deal.company_id ?? null)
  );

  async function save() {
    if (!deal || !draft) return;
    if (!draft.name.trim()) {
      setFormError(`${v.A('deal')} needs a name.`);
      return;
    }
    const pence = poundsToPence(draft.amount === '' ? 0 : draft.amount);
    if (pence === null) {
      setFormError('The amount must be a number of pounds, from 0.');
      return;
    }
    setFormError('');
    try {
      await update.mutateAsync({
        id: deal.id,
        body: {
          name: draft.name.trim(),
          amount_pence: pence,
          close_date: draft.close,
          owner_user_id: draft.owner ?? '',
          company_id: draft.companyId ?? '',
        },
      });
      notifications.show({ color: 'teal', title: 'Saved', message: draft.name.trim() });
    } catch (e) {
      setFormError((e as Error).message);
    }
  }

  async function del() {
    if (!deal) return;
    if (!window.confirm(`Delete ${deal.name}? Its history goes with it. This cannot be undone.`)) return;
    try {
      await remove.mutateAsync(deal.id);
      onClose();
    } catch (e) {
      setFormError((e as Error).message);
    }
  }

  return (
    <Drawer
      opened={id !== null}
      onClose={onClose}
      position="right"
      size="lg"
      title={deal ? deal.name : v.One('deal')}
      styles={{ title: { fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' } }}
    >
      {error && <Alert color="red" variant="light">{error.message}</Alert>}
      {isPending && id && <Group justify="center" p="xl"><Loader /></Group>}
      {deal && draft && (
        <Stack gap="md">
          <Group gap={8} wrap="wrap">
            <Badge variant="light" color={statusColour(deal.status)}>{STATUS_LABEL[deal.status]}</Badge>
            <MonoLabel>{`${deal.pipeline_name} · ${formatPence(deal.amount_pence)}`}</MonoLabel>
          </Group>
          {deal.status === 'lost' && deal.lost_reason && (
            <Alert variant="light" color="red" title="Why it was lost">{deal.lost_reason}</Alert>
          )}
          {formError && <Alert color="red" variant="light">{formError}</Alert>}

          <Select
            label={v.One('stage')}
            data={stages.map((s) => ({ value: s.id, label: s.name }))}
            value={deal.stage_id}
            allowDeselect={false}
            disabled={!canEdit}
            onChange={(sid) => {
              const s = stages.find((x) => x.id === sid);
              if (s) mover.request(deal, s);
            }}
          />
          <TextInput
            label="Name"
            disabled={!canEdit}
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.currentTarget.value })}
          />
          <Group grow align="flex-start" wrap="wrap">
            <NumberInput
              label="Amount"
              prefix="£"
              thousandSeparator=","
              decimalScale={2}
              min={0}
              allowNegative={false}
              disabled={!canEdit}
              value={draft.amount}
              onChange={(a) => setDraft({ ...draft, amount: a })}
              miw={140}
            />
            <TextInput
              label="Close date"
              type="date"
              disabled={!canEdit}
              value={draft.close}
              onChange={(e) => setDraft({ ...draft, close: e.currentTarget.value })}
              miw={140}
            />
          </Group>
          <OwnerSelect
            label="Owner"
            disabled={!canEdit}
            value={draft.owner}
            onChange={(o) => setDraft({ ...draft, owner: o })}
          />
          <CompanySelect
            label={v.One('company')}
            disabled={!canEdit}
            value={draft.companyId}
            valueLabel={draft.companyName}
            onChange={(cid, n) => setDraft({ ...draft, companyId: cid, companyName: n })}
          />
          {deal.company_id && (
            <Anchor component={Link} to={`/companies/${deal.company_id}`} size="sm">
              {`Open ${deal.company_name ?? `the ${v.one('company')}`}`}
            </Anchor>
          )}
          {deal.source_submission_id && (
            <Text size="xs" c="dimmed">
              {`Created from ${v.a('lead')} (${deal.source_submission_id.slice(0, 8)}).`}
            </Text>
          )}
          {canEdit && (
            <Group justify="space-between">
              <Button variant="subtle" color="red" onClick={del} loading={remove.isPending}>
                {`Delete ${v.one('deal')}`}
              </Button>
              <Button onClick={save} disabled={!dirty} loading={update.isPending}>Save</Button>
            </Group>
          )}

          {perms.recordProperties && (
            <PropertiesPanel
              object="deal"
              values={(deal.properties ?? {}) as Record<string, unknown>}
              canEdit={canEdit}
              saving={update.isPending}
              onSave={(patch) => update.mutateAsync({ id: deal.id, body: { properties: patch } })}
            />
          )}

          {/* The stage history is part of the timeline: every move, with its
              reason, between the notes, tasks and calls. A note written here
              also sits on the deal's company. */}
          <Divider label="Timeline" labelPosition="left" />
          <TimelinePanel
            objectType="deal"
            objectId={deal.id}
            extraLinks={deal.company_id ? [{ object_type: 'company', object_id: deal.company_id }] : []}
          />
        </Stack>
      )}
      {mover.dialog}
    </Drawer>
  );
}
